import { Clear, FilterAlt } from '@mui/icons-material'
import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  Menu,
  OutlinedInput,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { GetUsersRequest, PlaceNode } from '@ulysses-inc/harami_api_client'
import React, { useEffect, useState } from 'react'
import { usePlaceLoadingStatus } from 'src/exShared/hooks/usePlaceLoadingStatus'
import {
  splitAccessiblePlaceNode,
  splitAllPlaceNodes,
} from 'src/exShared/libraries/filterDropDown/place'
import { mergedPlaceNodeIds } from 'src/exShared/util/place/placeNode'
import { splitLoginPlaceUUIDs } from 'src/exShared/util/place/splitLoginPlace'
import { useListFilter } from 'src/hooks/filter/useListFilter'
import { GetUsersFilter } from 'src/state/ducks/users/types'
import { ListFilter } from '../../views/types/utils/ListFilter'

const PLACE_TOGGLE_INPUT_ID = 'users-filter-place-toggle'

interface OwnProps {
  places: PlaceNode[]
  placeNodes: PlaceNode[]
  request?: GetUsersRequest
  filters: ListFilter | null
  setUserFilter: (filter?: GetUsersFilter) => void
  updatedUsers: boolean
  isLoadingUser?: boolean
  /**
   * Feature Flag `userListSortAndFilter` の判定結果。判定中は undefined。
   * true のとき、初期表示でログインユーザーの所属現場に暗黙で絞り込むのをやめる。
   */
  canUseNewPlaceFilter?: boolean
  /** トグル操作で表示件数が大きく変わるため、一覧を 1 ページ目に戻す */
  resetPagination: () => void
}

const UsersFilterDropDown: React.FC<OwnProps> = (props: OwnProps) => {
  const [userName, setUserName] = useState<string>('')
  const [placeNodeIds, setPlaceNodeIds] = useState<string[]>([])
  const [placeGroupNodeIds, setPlaceGroupNodeIds] = useState<string[]>([])
  const [accessiblePlaces, setAccessiblePlaces] = useState<PlaceNode[]>([])
  const [accessiblePlaceGroups, setAccessiblePlaceGroups] = useState<
    PlaceNode[]
  >([])
  // 現場トグルの ON/OFF。既定はどちらの Feature Flag 状態でも OFF だが、意味が反転する。
  //   FF ON  : ON = 自分が所属する現場のユーザーのみ表示 (OFF = 企業内の全ユーザーを表示)
  //   FF OFF : ON = 所属現場以外のユーザーも表示         (OFF = 所属現場のユーザーのみ表示)
  // FF 削除時は isShowOnlyMyPlaces にリネームし、FF OFF 側の分岐をすべて消す。
  const [isToggleOn, setIsToggleOn] = useState<boolean>(false)
  const { filters, setListFilter } = useListFilter()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = anchorEl !== null
  const { loginPlaceGroupIds, loginPlaceIds } = splitLoginPlaceUUIDs()
  const placeLoadingStatus = usePlaceLoadingStatus()

  const isFeatureResolved = props.canUseNewPlaceFilter !== undefined
  const canUseNewPlaceFilter = props.canUseNewPlaceFilter === true

  // 初回及び、トグル切り替え時に絞り込みで選択可能な現場をセット。
  // FF ON では常に全現場を選択肢に出す。トグルはあくまで既定の絞り込みであって、
  // 選択できる現場を制限するものではないため (キオスク端末一覧と同じ考え方)
  useEffect(() => {
    const accessiblePlaceNode =
      canUseNewPlaceFilter || isToggleOn
        ? splitAllPlaceNodes(props.placeNodes, props.places)
        : splitAccessiblePlaceNode(props.placeNodes, props.places)
    // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
    setAccessiblePlaces(accessiblePlaceNode.accessiblePlaces)
    setAccessiblePlaceGroups(accessiblePlaceNode.accessiblePlaceGroups)
  }, [props.placeNodes, props.places, isToggleOn, canUseNewPlaceFilter])

  // 絞り込み条件の変更検知して再取得
  useEffect(() => {
    if (placeLoadingStatus !== 'finish') {
      return
    }
    // 更新が完了してない場合はリターン
    if (props.updatedUsers) {
      return
    }
    // Feature Flag の判定中に絞り込みを確定させると、'yes' に解決した後も旧仕様の条件で
    // 一覧を取得してしまう。判定が確定してから 1 回だけ確定させる
    if (!isFeatureResolved) {
      return
    }

    // eslint-disable-next-line react-hooks/immutability -- 関数の宣言順序による既存パターンのため、リファクタリング時に修正する
    const resolvedPlaceNodeIds = resolvePlaceNodeIds()
    // localStorage にはユーザーが明示的に選んだ条件だけを残す
    // eslint-disable-next-line react-hooks/immutability -- 関数の宣言順序による既存パターンのため、リファクタリング時に修正する
    saveFilter(userName, placeNodeIds, placeGroupNodeIds)
    props.setUserFilter({
      userName: { $like: userName },
      placeNodeId:
        resolvedPlaceNodeIds.length > 0
          ? { $in: resolvedPlaceNodeIds }
          : undefined,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.updatedUsers,
    userName,
    placeNodeIds,
    placeGroupNodeIds,
    isToggleOn,
    placeLoadingStatus,
    isFeatureResolved,
    canUseNewPlaceFilter,
  ])

  /** ログインユーザーの所属現場を、現場グループを子現場に展開したうえで返す */
  const mergedLoginPlaceNodeIds = (): string[] =>
    mergedPlaceNodeIds(
      props.placeNodes,
      loginPlaceIds,
      loginPlaceGroupIds,
      true,
    )

  /**
   * API に送る現場 ID を決める。
   *
   * FF ON : 現場/現場グループを明示的に選んでいればそれを使う。選んでいない場合は
   *   トグル ON のときだけログインユーザーの所属現場に絞り、トグル OFF (既定) では
   *   現場条件を送らず企業内の全ユーザーを表示する。
   * FF OFF: 現行挙動。明示選択がなければ、トグル OFF でログインユーザーの所属現場に
   *   暗黙で絞り込み、トグル ON で全現場を対象にする。FF 削除時にこの分岐ごと消す。
   */
  const resolvePlaceNodeIds = (): string[] => {
    const hasExplicitPlaceSelection =
      placeNodeIds.length > 0 || placeGroupNodeIds.length > 0
    if (hasExplicitPlaceSelection) {
      return mergedPlaceNodeIds(
        props.placeNodes,
        placeNodeIds,
        placeGroupNodeIds,
        true,
      )
    }
    if (canUseNewPlaceFilter) {
      return isToggleOn ? mergedLoginPlaceNodeIds() : []
    }
    return isToggleOn ? [] : mergedLoginPlaceNodeIds()
  }

  const isFilterOn = () =>
    userName !== '' ||
    placeGroupNodeIds.length > 0 ||
    placeNodeIds.length > 0 ||
    // FF ON のトグルは表示を絞る操作なので絞り込み ON と数える。
    // FF OFF のトグルは逆に表示を広げる操作なので数えない (現行挙動)
    (canUseNewPlaceFilter && isToggleOn)

  const clearFilter = () => {
    setPlaceNodeIds([])
    setPlaceGroupNodeIds([])
    setUserName('')
    if (canUseNewPlaceFilter) {
      setIsToggleOn(false)
      props.resetPagination()
    }
    saveFilter()
  }

  const saveFilter = (
    userName?: string,
    placeNodeIds?: string[],
    placeGroupNodeIds?: string[],
  ) => {
    setListFilter({
      ...(filters ?? {}),
      users: {
        pagination: props.request,
        filter: {
          userName: userName ?? '',
          placeNodeIds: placeNodeIds,
          placeGroupNodeIds: placeGroupNodeIds,
        },
      },
    })
  }

  const hoverArea = (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="flex-end">
        <Button onClick={clearFilter}>クリア</Button>
      </Stack>
      <Stack>
        <Typography variant="subtitle1">表示名</Typography>
        <OutlinedInput
          fullWidth
          size="small"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = event.target.value
            setUserName(newValue)
          }}
          value={userName}
          endAdornment={
            userName && (
              <InputAdornment position="end" variant="standard">
                <IconButton onClick={clearFilter} size="small" color="default">
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }
        />
      </Stack>
      <Stack>
        <Typography
          variant="subtitle1"
          component="label"
          htmlFor={PLACE_TOGGLE_INPUT_ID}
        >
          {canUseNewPlaceFilter
            ? '自分が所属する現場のユーザーのみ表示'
            : '所属現場以外のユーザーも表示'}
        </Typography>
        <Switch
          id={PLACE_TOGGLE_INPUT_ID}
          checked={isToggleOn}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setIsToggleOn(event.target.checked)
            props.resetPagination()
            if (!canUseNewPlaceFilter) {
              // 現行挙動: 選択できる現場がトグルで変わるため、選択済みの現場を捨てる。
              // FF ON では選択肢を常に全現場にしており、明示選択をトグルより優先するので捨てない
              setPlaceNodeIds([])
              setPlaceGroupNodeIds([])
            }
          }}
        />
      </Stack>
      <Stack>
        <Typography variant="subtitle1">現場グループ名</Typography>

        <Autocomplete<PlaceNode, true, false, false>
          multiple
          size="small"
          disableCloseOnSelect
          options={accessiblePlaceGroups}
          getOptionLabel={option => option.placeGroup?.name ?? '名称未設定'}
          value={placeGroupNodeIds
            .map(uuid =>
              accessiblePlaceGroups.find(placeNode => placeNode.uuid === uuid),
            )
            .filter(
              (placeNode): placeNode is PlaceNode => placeNode !== undefined,
            )}
          onChange={(_, newValue) => {
            setPlaceGroupNodeIds(newValue.map(placeNode => placeNode.uuid))
          }}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Stack direction="row" alignItems="center" spacing={1} mr={1}>
                <Checkbox size="small" checked={selected} />
                {option.placeGroup?.name}
              </Stack>
            </li>
          )}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField {...params} />
          )}
        />
      </Stack>
      <Stack>
        <Typography variant="subtitle1">現場名</Typography>

        <Autocomplete<PlaceNode, true, false, false>
          multiple
          size="small"
          disableCloseOnSelect
          options={accessiblePlaces}
          getOptionLabel={option => option.place?.name ?? '名称未設定'}
          value={placeNodeIds
            .map(uuid =>
              accessiblePlaces.find(placeNode => placeNode.uuid === uuid),
            )
            .filter(
              (placeNode): placeNode is PlaceNode => placeNode !== undefined,
            )}
          onChange={(_, newValue) => {
            setPlaceNodeIds(newValue.map(placeNode => placeNode.uuid))
          }}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Stack direction="row" alignItems="center" spacing={1} mr={1}>
                <Checkbox size="small" checked={selected} />
                {option.place?.name}
              </Stack>
            </li>
          )}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField {...params} />
          )}
        />
      </Stack>
    </Stack>
  )

  return (
    <Box>
      <Button
        color={isFilterOn() ? 'filtered' : 'secondary'}
        variant="outlined"
        startIcon={<FilterAlt />}
        onClick={({ currentTarget }) => {
          setAnchorEl(currentTarget)
        }}
      >
        絞り込み: {isFilterOn() ? 'ON' : 'OFF'}
      </Button>
      <Menu
        id="member-filter-menu"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => {
          setAnchorEl(null)
        }}
        slotProps={{
          paper: {
            sx: {
              padding: 2,
              width: '100%',
              maxWidth: 400,
            },
          },
          list: {
            sx: {
              padding: 0,
            },
          },
        }}
      >
        {hoverArea}
      </Menu>
    </Box>
  )
}

export default UsersFilterDropDown
